import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { S, Icon } from '../regureStyles';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const CreateModal = ({ onClose, onCreated }) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [documentFileId, setDocumentFileId] = useState('');
  const [signers, setSigners] = useState([{ name: '', email: '' }]);
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios
      .get(`${BASE_URL}/fileExchange`, { withCredentials: true })
      .then(r => {
        // Response structure: { status, data: { data: [...] } }
        const fileArray = r.data?.data?.data || r.data?.data || [];
        setFiles(Array.isArray(fileArray) ? fileArray : []);
      })
      .catch(() => setFiles([]))
      .finally(() => setLoadingFiles(false));
  }, []);

  const addSigner = () => setSigners(s => [...s, { name: '', email: '' }]);
  const removeSigner = i => setSigners(s => s.filter((_, idx) => idx !== i));
  const updateSigner = (i, field, val) => setSigners(s => s.map((sg, idx) => (idx === i ? { ...sg, [field]: val } : sg)));

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError(t('screens.esign.errTitle'));
      return;
    }
    if (!documentFileId) {
      setError(t('screens.esign.errDoc'));
      return;
    }
    if (signers.some(s => !s.name.trim() || !s.email.trim())) {
      setError(t('screens.esign.errSigners'));
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${BASE_URL}/signatures`,
        {
          title,
          description,
          documentFileId,
          signers,
        },
        { withCredentials: true },
      );
      onCreated(res.data.data);
    } catch (e) {
      setError(e.response?.data?.message || t('screens.esign.errCreate'));
    } finally {
      setSubmitting(false);
    }
  };

  const inp = (placeholder, value, onChange, type = 'text') => (
    <input
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{
        width: '100%',
        padding: '9px 12px',
        border: `1px solid ${S.borderLight}`,
        borderRadius: 8,
        fontSize: 14,
        outline: 'none',
        boxSizing: 'border-box',
        color: S.textPrimary,
        background: '#fff',
      }}
    />
  );

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 16,
          padding: 32,
          width: 560,
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div>
            <div style={{ fontSize: 11, color: S.textMuted, marginBottom: 2 }}>{t('screens.esign.modalEyebrow')}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: S.textPrimary }}>{t('screens.esign.modalTitle')}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <Icon name='x' color={S.textMuted} size={20} />
          </button>
        </div>

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', marginBottom: 16, color: '#991b1b', fontSize: 13 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: S.textSecondary, marginBottom: 6 }}>{t('screens.esign.titleLabel')}</label>
            {inp(t('screens.esign.titlePh'), title, setTitle)}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: S.textSecondary, marginBottom: 6 }}>{t('screens.esign.descriptionLabel')}</label>
            <textarea
              placeholder={t('screens.esign.descriptionPh')}
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={2}
              style={{
                width: '100%',
                padding: '9px 12px',
                border: `1px solid ${S.borderLight}`,
                borderRadius: 8,
                fontSize: 14,
                outline: 'none',
                boxSizing: 'border-box',
                color: S.textPrimary,
                resize: 'vertical',
                fontFamily: 'inherit',
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: S.textSecondary, marginBottom: 6 }}>{t('screens.esign.documentLabel')}</label>
            {loadingFiles ? (
              <div style={{ color: S.textMuted, fontSize: 13 }}>{t('screens.esign.loadingDocs')}</div>
            ) : (
              <select
                value={documentFileId}
                onChange={e => setDocumentFileId(e.target.value)}
                style={{
                  width: '100%',
                  padding: '9px 12px',
                  border: `1px solid ${S.borderLight}`,
                  borderRadius: 8,
                  fontSize: 14,
                  outline: 'none',
                  background: '#fff',
                  color: S.textPrimary,
                }}
              >
                <option value=''>{t('screens.esign.selectDoc')}</option>
                {files.length
                  ? files.map(f => (
                      <option key={f._id} value={f._id}>
                        {f.name || f.originalName}
                      </option>
                    ))
                  : null}
              </select>
            )}
          </div>

          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: S.textSecondary }}>{t('screens.esign.signersLabel')}</label>
              <button
                onClick={addSigner}
                style={{
                  fontSize: 12,
                  color: S.burgundy,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <Icon name='plus' color={S.burgundy} size={14} /> {t('screens.esign.addSigner')}
              </button>
            </div>
            {signers.map((sg, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, marginBottom: 8 }}>
                {inp(t('screens.esign.signerNamePh'), sg.name, v => updateSigner(i, 'name', v))}
                {inp(t('screens.esign.signerEmailPh'), sg.email, v => updateSigner(i, 'email', v), 'email')}
                {signers.length > 1 && (
                  <button onClick={() => removeSigner(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 8px', color: S.textMuted }}>
                    <Icon name='x' color='#ef4444' size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              background: S.bgCard,
              border: `1px solid ${S.borderLight}`,
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 500,
              color: S.textSecondary,
              cursor: 'pointer',
            }}
          >
            {t('screens.esign.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              padding: '10px 24px',
              background: S.burgundy,
              border: 'none',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600,
              color: '#fff',
              cursor: submitting ? 'default' : 'pointer',
              opacity: submitting ? 0.7 : 1,
            }}
          >
            {submitting ? t('screens.esign.sending') : t('screens.esign.sendForSignature')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateModal;
